export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

export const ACCEPTED_FILE_COPY =
  'Choose a PDF, TXT, Markdown, CSV, Word (.docx), PNG, or JPEG file. Up to 50 MB.';

export const ACCEPTED_FILE_TYPES =
  '.pdf,.txt,.md,.csv,.docx,.png,.jpg,.jpeg,application/pdf,text/plain,text/markdown,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg';

const allowedUploadExtensions = [
  '.pdf',
  '.txt',
  '.md',
  '.csv',
  '.docx',
  '.png',
  '.jpg',
  '.jpeg',
];

const allowedUploadMimeTypes = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
];

export const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
};

const validateUploadFile = (file: File | null) => {
  if (!file) return ACCEPTED_FILE_COPY;
  if (file.size > MAX_FILE_SIZE_BYTES) return 'File must be 50 MB or smaller.';

  const name = file.name.toLowerCase();
  const hasAllowedExtension = allowedUploadExtensions.some((extension) =>
    name.endsWith(extension),
  );
  if (!hasAllowedExtension) {
    return 'File must end in .pdf, .txt, .md, .csv, .docx, .png, .jpg, or .jpeg.';
  }

  if (file.type && !allowedUploadMimeTypes.includes(file.type)) {
    return `Unsupported content type '${file.type}'.`;
  }

  if (file.type === 'application/vnd.ms-excel' && !name.endsWith('.csv')) {
    return 'Spreadsheet uploads must use a .csv extension.';
  }

  return '';
};

const hasBytes = (bytes: Uint8Array, expected: number[]) =>
  expected.every((value, index) => bytes[index] === value);

const validateUploadFileContents = async (file: File | null) => {
  const basicError = validateUploadFile(file);
  if (basicError || !file) return basicError;

  const name = file.name.toLowerCase();
  const headerBytes = new Uint8Array(
    await file.slice(0, Math.min(file.size, 8)).arrayBuffer(),
  );

  if (name.endsWith('.pdf')) {
    if (!hasBytes(headerBytes, [0x25, 0x50, 0x44, 0x46, 0x2d])) {
      return 'File is not a valid PDF (missing %PDF- header).';
    }
  }

  if (name.endsWith('.docx') && !hasBytes(headerBytes, [0x50, 0x4b])) {
    return 'File is not a valid DOCX document.';
  }

  if (
    name.endsWith('.png') &&
    !hasBytes(headerBytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
  ) {
    return 'File is not a valid PNG image.';
  }

  if (
    (name.endsWith('.jpg') || name.endsWith('.jpeg')) &&
    !hasBytes(headerBytes, [0xff, 0xd8, 0xff])
  ) {
    return 'File is not a valid JPEG image.';
  }

  return '';
};

export const validateUploadFiles = async (files: File[]) => {
  if (!files.length) return ACCEPTED_FILE_COPY;

  for (const file of files) {
    const error = await validateUploadFileContents(file);
    if (error) return `${file.name}: ${error}`;
  }

  return '';
};

export const validateUploadFilesBasic = (files: File[]) => {
  if (!files.length) return ACCEPTED_FILE_COPY;

  for (const file of files) {
    const error = validateUploadFile(file);
    if (error) return `${file.name}: ${error}`;
  }

  return '';
};
